/**
 * Nightwave — audio-player.js
 * Global beat player: a single <audio> element for the whole page, so only
 * one beat plays at a time. Any element with [data-play-button] and the
 * attributes from snippets/track-attributes.liquid can start playback.
 *
 * Emits on document:
 *   theme:player:state  { track, playing, loading }
 *   theme:player:time   { id, currentTime, duration, progress }
 *   theme:player:peaks  { id, peaks }
 */
(() => {
  'use strict';

  const theme = (window.theme = window.theme || {});
  const strings = theme.strings || {};
  const settings = theme.settings || {};
  const formatTime =
    (theme.utils && theme.utils.formatTime) ||
    ((seconds) => {
      if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
      return `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
    });

  const PEAK_COUNT = 160;
  const VOLUME_KEY = 'nightwave:volume';

  const readTrack = (element) => ({
    id: element.dataset.trackId,
    src: element.dataset.trackSrc,
    title: element.dataset.trackTitle || '',
    meta: element.dataset.trackMeta || '',
    url: element.dataset.trackUrl || '',
    cover: element.dataset.trackCover || '',
    price: element.dataset.trackPrice || '',
  });

  const storage = {
    get(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (error) {
        /* Storage unavailable (private mode) */
      }
    },
  };

  const toRgba = (color, alpha) => {
    const hex = String(color).trim().replace('#', '');
    if (/^[0-9a-f]{3}$/i.test(hex) || /^[0-9a-f]{6}$/i.test(hex)) {
      const full = hex.length === 3 ? hex.replace(/./g, '$&$&') : hex;
      const value = parseInt(full, 16);
      return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`;
    }
    return `rgba(255, 255, 255, ${alpha})`;
  };

  /* Waveform peaks
     ======================================================================== */
  const Peaks = {
    cache: new Map(),

    /** Deterministic "musical" waveform generated from a seed (the product id). */
    generated(seed, count = PEAK_COUNT) {
      let state = 2166136261;
      String(seed || 'nightwave')
        .split('')
        .forEach((char) => {
          state = Math.imul(state ^ char.charCodeAt(0), 16777619) >>> 0;
        });
      const random = () => {
        state ^= state << 13;
        state >>>= 0;
        state ^= state >> 17;
        state ^= state << 5;
        state >>>= 0;
        return state / 4294967296;
      };

      const peaks = [];
      let previous = 0.5;
      for (let i = 0; i < count; i += 1) {
        const t = i / count;
        const section = (t > 0.22 && t < 0.46) || (t > 0.62 && t < 0.88) ? 0.18 : 0;
        const envelope = 0.5 + 0.3 * Math.sin(t * Math.PI) + section;
        const value = Math.min(1, Math.max(0.1, (previous * 0.3 + random() * 0.7) * envelope));
        peaks.push(value);
        previous = value;
      }
      return peaks;
    },

    /** Real peaks decoded from the audio file (opt-in: Theme settings > Audio player). */
    async analyze(src, count = PEAK_COUNT) {
      if (this.cache.has(src)) return this.cache.get(src);

      const storageKey = `nightwave:peaks:${src}`;
      try {
        const stored = window.sessionStorage.getItem(storageKey);
        if (stored) {
          const parsed = JSON.parse(stored);
          this.cache.set(src, parsed);
          return parsed;
        }
      } catch (error) {
        /* ignore */
      }

      const OfflineContext = window.OfflineAudioContext || window.webkitOfflineAudioContext;
      if (!OfflineContext) throw new Error('Web Audio unavailable');

      const response = await fetch(src);
      if (!response.ok) throw new Error(`Unable to fetch preview (${response.status})`);
      const arrayBuffer = await response.arrayBuffer();

      // Decoding at a low sample rate keeps memory usage small
      const context = new OfflineContext(1, 1, 11025);
      const audioBuffer = await new Promise((resolve, reject) => {
        context.decodeAudioData(arrayBuffer, resolve, reject);
      });

      const data = audioBuffer.getChannelData(0);
      const blockSize = Math.max(1, Math.floor(data.length / count));
      const peaks = [];
      let max = 0;
      for (let i = 0; i < count; i += 1) {
        let peak = 0;
        const start = i * blockSize;
        const end = Math.min(start + blockSize, data.length);
        for (let j = start; j < end; j += 2) {
          const value = Math.abs(data[j]);
          if (value > peak) peak = value;
        }
        peaks.push(peak);
        if (peak > max) max = peak;
      }

      const normalized = peaks.map((peak) => Math.max(0.06, max ? Math.round((peak / max) * 100) / 100 : 0.06));
      this.cache.set(src, normalized);
      try {
        window.sessionStorage.setItem(storageKey, JSON.stringify(normalized));
      } catch (error) {
        /* ignore quota errors */
      }
      return normalized;
    },
  };

  /* Waveform renderer (canvas)
     ======================================================================== */
  class Waveform {
    constructor(container, seed) {
      this.container = container;
      this.canvas = container.querySelector('canvas');
      this.context = this.canvas.getContext('2d');
      this.peaks = Peaks.generated(seed || container.dataset.seed);
      this.progress = 0;
      this.hover = null;

      this.resizeObserver = new ResizeObserver(() => this.draw());
      this.resizeObserver.observe(container);

      container.addEventListener('pointermove', (event) => {
        if (event.pointerType !== 'mouse') return;
        const rect = container.getBoundingClientRect();
        this.hover = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
        this.draw();
      });
      container.addEventListener('pointerleave', () => {
        this.hover = null;
        this.draw();
      });

      this.draw();
    }

    destroy() {
      this.resizeObserver.disconnect();
    }

    setPeaks(peaks) {
      this.peaks = peaks;
      this.draw();
    }

    setProgress(progress) {
      const clamped = Math.min(1, Math.max(0, progress || 0));
      if (Math.abs(clamped - this.progress) < 0.0005 && this.drawn) return;
      this.progress = clamped;
      this.draw();
    }

    colors() {
      const styles = getComputedStyle(document.documentElement);
      const accent = styles.getPropertyValue('--accent').trim() || '#6c55ff';
      const text = styles.getPropertyValue('--text').trim() || '#ffffff';
      return {
        played: accent,
        idle: toRgba(text, 0.2),
        hover: toRgba(text, 0.42),
      };
    }

    draw() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        const width = this.container.clientWidth;
        const height = this.container.clientHeight;
        if (!width || !height) return;

        const ratio = Math.min(window.devicePixelRatio || 1, 2);
        const pixelWidth = Math.round(width * ratio);
        const pixelHeight = Math.round(height * ratio);
        if (this.canvas.width !== pixelWidth || this.canvas.height !== pixelHeight) {
          this.canvas.width = pixelWidth;
          this.canvas.height = pixelHeight;
        }

        const ctx = this.context;
        ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
        ctx.clearRect(0, 0, width, height);

        if (!this.palette) this.palette = this.colors();
        const barWidth = width < 360 ? 2 : 3;
        const gap = 2;
        const count = Math.max(1, Math.floor((width + gap) / (barWidth + gap)));

        for (let i = 0; i < count; i += 1) {
          const peak = this.peaks[Math.floor((i / count) * this.peaks.length)] || 0.1;
          const barHeight = Math.max(2, peak * height);
          const x = i * (barWidth + gap);
          const y = (height - barHeight) / 2;
          const position = (i + 0.5) / count;

          if (position <= this.progress) ctx.fillStyle = this.palette.played;
          else if (this.hover !== null && position <= this.hover) ctx.fillStyle = this.palette.hover;
          else ctx.fillStyle = this.palette.idle;

          ctx.beginPath();
          if (typeof ctx.roundRect === 'function') ctx.roundRect(x, y, barWidth, barHeight, barWidth / 2);
          else ctx.rect(x, y, barWidth, barHeight);
          ctx.fill();
        }
        this.drawn = true;
      });
    }
  }

  /* Global sticky player
     ======================================================================== */
  class BeatPlayer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.audio = this.querySelector('[data-player-audio]');
      this.elements = {
        cover: this.querySelector('[data-player-cover]'),
        title: this.querySelector('[data-player-title]'),
        meta: this.querySelector('[data-player-meta]'),
        links: [...this.querySelectorAll('[data-player-link]')],
        toggle: this.querySelector('[data-player-toggle]'),
        prev: this.querySelector('[data-player-prev]'),
        next: this.querySelector('[data-player-next]'),
        current: this.querySelector('[data-player-current]'),
        duration: this.querySelector('[data-player-duration]'),
        seek: this.querySelector('[data-player-seek]'),
        volume: this.querySelector('[data-player-volume]'),
        mute: this.querySelector('[data-player-mute]'),
        close: this.querySelector('[data-player-close]'),
        progressLine: this.querySelector('[data-player-progress-line]'),
      };

      this.track = null;
      this.queue = [];
      this.loading = false;
      this.pendingSeek = null;
      this.waveform = new Waveform(this.querySelector('[data-waveform]'), 'nightwave');

      this.initVolume();
      this.bindAudioEvents();
      this.bindControls();

      this.onDocumentClick = this.onDocumentClick.bind(this);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('theme:tracks:changed', () => this.syncButtons());

      this.resizeObserver = new ResizeObserver(() => this.updateHeight());
      this.resizeObserver.observe(this);

      theme.player = this;
    }

    /* Setup ------------------------------------------------------------- */

    initVolume() {
      const stored = parseFloat(storage.get(VOLUME_KEY));
      const fallback = Number.isFinite(settings.playerVolume) ? settings.playerVolume : 0.8;
      this.audio.volume = Number.isFinite(stored) ? Math.min(1, Math.max(0, stored)) : fallback;
      this.updateVolumeUI();
    }

    bindAudioEvents() {
      const audio = this.audio;
      audio.addEventListener('play', () => this.syncButtons());
      audio.addEventListener('playing', () => {
        this.setLoading(false);
        this.startTicking();
      });
      audio.addEventListener('pause', () => {
        this.stopTicking();
        this.syncButtons();
      });
      audio.addEventListener('waiting', () => this.setLoading(true));
      audio.addEventListener('canplay', () => this.setLoading(false));
      audio.addEventListener('loadedmetadata', () => {
        if (this.pendingSeek !== null && Number.isFinite(audio.duration)) {
          audio.currentTime = this.pendingSeek * audio.duration;
          this.pendingSeek = null;
        }
        this.updateTime();
      });
      audio.addEventListener('durationchange', () => this.updateTime());
      audio.addEventListener('timeupdate', () => {
        if (audio.paused) this.updateTime();
      });
      audio.addEventListener('ended', () => {
        this.stopTicking();
        if (settings.autoplayNext !== false && this.getSibling(1)) this.step(1);
        else {
          audio.currentTime = 0;
          this.updateTime();
          this.syncButtons();
        }
      });
      audio.addEventListener('error', () => {
        if (!this.track) return;
        this.setLoading(false);
        this.stopTicking();
        this.syncButtons();
        if (theme.utils) theme.utils.announce(strings.noPreview);
      });
      audio.addEventListener('volumechange', () => this.updateVolumeUI());
    }

    bindControls() {
      const { toggle, prev, next, seek, volume, mute, close } = this.elements;

      toggle?.addEventListener('click', () => this.toggle());
      prev?.addEventListener('click', () => this.step(-1));
      next?.addEventListener('click', () => this.step(1));
      close?.addEventListener('click', () => this.hide());

      seek?.addEventListener('input', () => this.seek(seek.value / 1000));

      volume?.addEventListener('input', () => {
        const value = Number(volume.value) / 100;
        this.audio.volume = value;
        this.audio.muted = value === 0;
        storage.set(VOLUME_KEY, String(value));
      });

      mute?.addEventListener('click', () => {
        if (this.audio.muted && this.audio.volume === 0) this.audio.volume = 0.6;
        this.audio.muted = !this.audio.muted;
      });

      if ('mediaSession' in navigator) {
        const handlers = {
          play: () => this.play(),
          pause: () => this.pause(),
          previoustrack: () => this.step(-1),
          nexttrack: () => this.step(1),
        };
        Object.entries(handlers).forEach(([action, handler]) => {
          try {
            navigator.mediaSession.setActionHandler(action, handler);
          } catch (error) {
            /* Action not supported */
          }
        });
      }
    }

    /* Public API -------------------------------------------------------- */

    onDocumentClick(event) {
      const button = event.target.closest('[data-play-button]');
      if (!button) return;
      event.preventDefault();

      const track = readTrack(button);
      if (!track.src) return;

      if (this.track && this.track.id === track.id) {
        this.toggle();
        return;
      }
      this.queue = this.buildQueue(button);
      this.load(track, { autoplay: true });
    }

    buildQueue(origin) {
      const collect = (root) => {
        const seen = new Set();
        const queue = [];
        root.querySelectorAll('[data-play-button]').forEach((element) => {
          const track = readTrack(element);
          if (!track.src || seen.has(track.id)) return;
          seen.add(track.id);
          queue.push(track);
        });
        return queue;
      };

      const list = origin && origin.closest('[data-track-list]');
      const queue = list ? collect(list) : [];
      return queue.length > 1 ? queue : collect(document);
    }

    load(track, { autoplay = true, seek = null } = {}) {
      this.track = track;
      this.pendingSeek = seek;
      this.show();

      this.audio.src = track.src;
      this.setLoading(true);
      this.updateTrackInfo();
      this.waveform.setPeaks(Peaks.generated(track.id));
      this.waveform.setProgress(0);
      this.updateTime();

      if (autoplay) this.play();

      if (settings.waveform === 'analyzed') {
        this.audio.addEventListener(
          'canplay',
          () => {
            Peaks.analyze(track.src)
              .then((peaks) => {
                if (!this.track || this.track.id !== track.id) return;
                this.waveform.setPeaks(peaks);
                this.emit('peaks', { id: track.id, peaks });
              })
              .catch(() => {
                /* CORS or decoding error: keep the stylized waveform */
              });
          },
          { once: true }
        );
      }

      this.syncButtons();
    }

    play() {
      if (!this.track) return;
      this.show();
      const promise = this.audio.play();
      if (promise && typeof promise.catch === 'function') {
        promise.catch((error) => {
          if (error.name === 'AbortError') return;
          this.setLoading(false);
          this.syncButtons();
        });
      }
    }

    pause() {
      this.audio.pause();
    }

    toggle() {
      if (!this.track) return;
      if (this.audio.paused) this.play();
      else this.pause();
    }

    seek(ratio) {
      if (!this.track) return;
      const duration = this.audio.duration;
      if (Number.isFinite(duration) && duration > 0) {
        this.audio.currentTime = Math.min(1, Math.max(0, ratio)) * duration;
        this.updateTime();
      } else {
        this.pendingSeek = ratio;
      }
    }

    getSibling(direction) {
      if (!this.track) return null;
      const index = this.queue.findIndex((track) => track.id === this.track.id);
      if (index === -1) return null;
      return this.queue[index + direction] || null;
    }

    step(direction) {
      if (!this.track) return;
      if (direction < 0 && this.audio.currentTime > 3) {
        this.audio.currentTime = 0;
        return;
      }
      const sibling = this.getSibling(direction);
      if (sibling) this.load(sibling, { autoplay: true });
    }

    show() {
      if (!this.hidden && this.classList.contains('is-visible')) return;
      this.hidden = false;
      document.body.classList.add('has-player');
      void this.offsetHeight;
      this.classList.add('is-visible');
      this.updateHeight();
    }

    hide() {
      this.pause();
      this.classList.remove('is-visible');
      document.body.classList.remove('has-player');
      document.documentElement.style.setProperty('--player-height', '0px');
      setTimeout(() => {
        if (!this.classList.contains('is-visible')) this.hidden = true;
      }, 550);
    }

    /* UI updates -------------------------------------------------------- */

    emit(name, detail) {
      document.dispatchEvent(new CustomEvent(`theme:player:${name}`, { detail }));
    }

    updateHeight() {
      if (this.hidden) return;
      document.documentElement.style.setProperty('--player-height', `${this.offsetHeight}px`);
    }

    setLoading(loading) {
      this.loading = loading;
      this.syncButtons();
    }

    updateTrackInfo() {
      const { cover, title, meta, links } = this.elements;
      const track = this.track;

      title.textContent = track.title;
      meta.textContent = track.meta;
      links.forEach((link) => {
        if (track.url) link.href = track.url;
      });

      if (track.cover) {
        cover.src = track.cover;
        cover.alt = track.title;
        cover.hidden = false;
      } else {
        cover.hidden = true;
      }

      if ('mediaSession' in navigator && 'MediaMetadata' in window) {
        navigator.mediaSession.metadata = new window.MediaMetadata({
          title: track.title,
          artist: track.meta,
          artwork: track.cover ? [{ src: track.cover, sizes: '180x180' }] : [],
        });
      }
    }

    updateTime() {
      const { current, duration, seek, progressLine } = this.elements;
      const total = this.audio.duration;
      const time = this.audio.currentTime || 0;
      const progress = Number.isFinite(total) && total > 0 ? time / total : 0;

      current.textContent = formatTime(time);
      duration.textContent = Number.isFinite(total) ? formatTime(total) : '0:00';
      if (seek && document.activeElement !== seek) seek.value = String(Math.round(progress * 1000));
      if (progressLine) progressLine.style.transform = `scaleX(${progress})`;
      this.waveform.setProgress(progress);

      if (this.track) {
        this.emit('time', {
          id: this.track.id,
          currentTime: time,
          duration: Number.isFinite(total) ? total : 0,
          progress,
        });
      }
    }

    startTicking() {
      this.stopTicking();
      const tick = () => {
        this.updateTime();
        this.tickFrame = requestAnimationFrame(tick);
      };
      this.tickFrame = requestAnimationFrame(tick);
    }

    stopTicking() {
      if (this.tickFrame) cancelAnimationFrame(this.tickFrame);
      this.tickFrame = null;
    }

    updateVolumeUI() {
      const { volume, mute } = this.elements;
      const value = this.audio.muted ? 0 : Math.round(this.audio.volume * 100);
      if (volume) {
        volume.value = String(value);
        volume.style.setProperty('--range-progress', `${value}%`);
      }
      this.classList.toggle('is-muted', this.audio.muted || this.audio.volume === 0);
      mute?.setAttribute('aria-label', this.audio.muted ? strings.unmute || 'Unmute' : strings.mute || 'Mute');
    }

    syncButtons() {
      const playing = Boolean(this.track) && !this.audio.paused;
      const { toggle, prev, next } = this.elements;

      this.classList.toggle('is-playing', playing);
      this.classList.toggle('is-loading', this.loading && playing);
      toggle?.setAttribute('aria-label', playing ? strings.pause || 'Pause' : strings.play || 'Play');
      if (prev) prev.disabled = !this.track;
      if (next) next.disabled = !this.getSibling(1);

      document.querySelectorAll('[data-play-button]').forEach((button) => {
        const active = Boolean(this.track) && button.dataset.trackId === this.track.id;
        button.classList.toggle('is-active', active);
        button.classList.toggle('is-playing', active && playing);
        button.classList.toggle('is-loading', active && playing && this.loading);
        button.setAttribute('aria-pressed', String(active && playing));
      });

      if ('mediaSession' in navigator) navigator.mediaSession.playbackState = playing ? 'playing' : 'paused';
      this.emit('state', { track: this.track, playing, loading: this.loading });
    }
  }

  /* Inline player on the beat page
     Mirrors the global player when it plays this beat.
     ======================================================================== */
  class WaveformPlayer extends HTMLElement {
    connectedCallback() {
      this.trackId = this.dataset.trackId;
      this.button = this.querySelector('[data-play-button]');
      this.currentLabel = this.querySelector('[data-inline-current]');
      this.durationLabel = this.querySelector('[data-inline-duration]');
      this.range = this.querySelector('[data-inline-seek]');
      this.waveform = new Waveform(this.querySelector('[data-waveform]'), this.trackId);

      this.onState = this.onState.bind(this);
      this.onTime = this.onTime.bind(this);
      this.onPeaks = this.onPeaks.bind(this);
      document.addEventListener('theme:player:state', this.onState);
      document.addEventListener('theme:player:time', this.onTime);
      document.addEventListener('theme:player:peaks', this.onPeaks);

      this.range?.addEventListener('input', () => {
        const player = theme.player;
        if (!player || !this.button) return;
        const ratio = this.range.value / 1000;
        if (player.track && player.track.id === this.trackId) {
          player.seek(ratio);
        } else {
          player.queue = player.buildQueue(this.button);
          player.load(readTrack(this.button), { autoplay: true, seek: ratio });
        }
      });

      if (theme.player) theme.player.syncButtons();
    }

    disconnectedCallback() {
      document.removeEventListener('theme:player:state', this.onState);
      document.removeEventListener('theme:player:time', this.onTime);
      document.removeEventListener('theme:player:peaks', this.onPeaks);
      this.waveform?.destroy();
    }

    onState(event) {
      const active = Boolean(event.detail.track) && event.detail.track.id === this.trackId;
      this.classList.toggle('is-active', active);
      if (!active && this.wasActive) {
        this.waveform.setProgress(0);
        if (this.currentLabel) this.currentLabel.textContent = '0:00';
      }
      this.wasActive = active;
    }

    onTime(event) {
      const { id, currentTime, duration, progress } = event.detail;
      if (id !== this.trackId) return;
      this.waveform.setProgress(progress);
      if (this.range && document.activeElement !== this.range) this.range.value = String(Math.round(progress * 1000));
      if (this.currentLabel) this.currentLabel.textContent = formatTime(currentTime);
      if (this.durationLabel && duration) this.durationLabel.textContent = formatTime(duration);
    }

    onPeaks(event) {
      if (event.detail.id === this.trackId) this.waveform.setPeaks(event.detail.peaks);
    }
  }

  if (!customElements.get('beat-player')) customElements.define('beat-player', BeatPlayer);
  if (!customElements.get('waveform-player')) customElements.define('waveform-player', WaveformPlayer);
})();
